import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './welcome-tour.css';
import { TOUR_STEPS } from './tourConfig';

const TOUR_DISMISSED_KEY = 'isoqar.questionnaireTourCompleted';

/**
 * Welcome tour for the questionnaire table page.
 *
 * Adapted from the customer portal's OnboardingTour component.
 * Uses localStorage only (no server-side persistence) since the
 * questionnaire app has no user-profile concept.
 *
 * @param {{ isReady: boolean }} props
 *   - isReady: true when the table has finished loading and data is fetched.
 */
export function WelcomeTour({ isReady, forceTrigger }) {
    // Track the active driver so cleanup can destroy it on unmount.
    const driverRef = useRef(null);
    // Track the last handled forceTrigger to prevent re-triggering on isReady toggle
    const handledTriggerRef = useRef(0);
    // Differentiates a user-initiated destroy (Finish or close X — mark
    // complete) from a cleanup-time destroy (component unmounting — leave
    // localStorage alone so the tour re-shows next time).
    const quietDestroyRef = useRef(false);

    useEffect(() => {
        if (!isReady) return;

        const localCompleted =
            window.localStorage.getItem(TOUR_DISMISSED_KEY) === '1';

        const isNewTrigger = forceTrigger && forceTrigger !== handledTriggerRef.current;
        if (localCompleted && !isNewTrigger) return;
        
        if (isNewTrigger) {
            handledTriggerRef.current = forceTrigger;
        }

        // Build driver.js step array from our config.
        const driverSteps = TOUR_STEPS.map((step) => ({
            element: step.target,
            popover: {
                title: step.title,
                description: step.body,
                side: step.side,
                align: step.align,
            },
        }));

        const startTour = () => {
            // Reset scroll so highlighted elements aren't off-screen.
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

            driverRef.current = driver({
                showProgress: true,
                progressText: '{{current}} of {{total}}',
                nextBtnText: 'Next',
                prevBtnText: 'Back',
                doneBtnText: 'Finish',
                allowClose: true,
                overlayOpacity: 0.6,
                stagePadding: 8,
                stageRadius: 6,
                steps: driverSteps,
                onDestroyed: () => {
                    if (quietDestroyRef.current) {
                        quietDestroyRef.current = false;
                        return;
                    }
                    // User finished or dismissed the tour — persist completion.
                    try {
                        window.localStorage.setItem(TOUR_DISMISSED_KEY, '1');
                    } catch {
                        // localStorage unavailable — tour will re-show next time.
                    }
                },
            });
            driverRef.current.drive();
        };

        // Wait for the first targeted element to land in the DOM before
        // starting. If it's already there, start immediately; otherwise
        // watch for it via MutationObserver. The tour starts the moment the
        // page is ready — no timers, no fixed delays.
        const readySelector = TOUR_STEPS.find((s) => s.target)?.target;
        let observer = null;

        if (!readySelector || document.querySelector(readySelector)) {
            startTour();
        } else {
            observer = new MutationObserver(() => {
                if (document.querySelector(readySelector)) {
                    observer?.disconnect();
                    observer = null;
                    startTour();
                }
            });
            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            observer?.disconnect();
            if (driverRef.current?.isActive()) {
                // Component is unmounting (user navigated into a questionnaire,
                // etc.) — destroy without recording completion so it re-shows.
                quietDestroyRef.current = true;
                driverRef.current.destroy();
            }
            driverRef.current = null;
        };
    }, [isReady, forceTrigger]);

    // Renderless component — the tour UI is managed by driver.js.
    return null;
}
